import type {Employee, NewEmployeeData} from '../types/employee';
import type {Contract} from '../types/contract';
import {toBankAccountPayload} from '../utils/bankAccounts';
import {API_BASE_URL} from '../config/apiConfig';


interface EmployeesResult {
    success: boolean;
    employees?: Employee[];
    error?: string;
}

interface AddEmployeeResult {
    success: boolean;
    employee?: Employee;
    error?: string;
    statusCode?: number;
    violations?: string[];
}

interface DeleteEmployeesResult {
    success: boolean;
    error?: string;
}

interface ActivateDeactivateResult {
    success: boolean;
    error?: string;
}

interface AddContractResult {
    success: boolean;
    contract?: Contract;
    error?: string;
    statusCode?: number;
    violations?: string[];
}

interface ContractsResult {
    success: boolean;
    contracts?: Contract[];
    totalRecords?: number;
    error?: string;
}

interface TerminateContractResult {
    success: boolean;
    contractId?: string;
    error?: string;
}

export const getEmployees = async (token: string): Promise<EmployeesResult> => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/employees`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || 'Failed to fetch employees');
        }

        const data = await response.json();
        return {
            success: true,
            employees: data,
        };
    } catch (error) {
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Network error occurred',
        };
    }
};

export const addEmployee = async (token: string, employeeData: NewEmployeeData): Promise<AddEmployeeResult> => {
    console.log('addEmployee service called with:', employeeData);
    console.log('API URL:', `${API_BASE_URL}/api/employees/add`);

    try {
        const response = await fetch(`${API_BASE_URL}/api/employees/add`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({...employeeData, bankAccount: toBankAccountPayload(employeeData.bankAccount)}),
        });

        console.log('Response status:', response.status);
        console.log('Response ok:', response.ok);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('Error response:', errorData);
            return {
                success: false,
                statusCode: response.status,
                error: errorData.message || 'Failed to add employee',
                violations: Array.isArray(errorData.violations)
                    ? errorData.violations
                    : Array.isArray(errorData.errors)
                        ? errorData.errors
                        : [],
            };
        }

        const data = await response.json();
        console.log('Success response data:', data);
        return {
            success: true,
            employee: data,
        };
    } catch (error) {
        console.error('addEmployee error:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Network error occurred',
        };
    }
};

export const updateEmployee = async (token: string, employeeId: number, employeeData: NewEmployeeData): Promise<AddEmployeeResult> => {
    console.log('updateEmployee service called with:', employeeId, employeeData);
    console.log('API URL:', `${API_BASE_URL}/api/employees/update/${employeeId}`);

    try {
        const response = await fetch(`${API_BASE_URL}/api/employees/update/${employeeId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(employeeData),
        });

        console.log('Response status:', response.status);
        console.log('Response ok:', response.ok);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('Error response:', errorData);
            return {
                success: false,
                statusCode: response.status,
                error: errorData.message || 'Failed to update employee',
                violations: Array.isArray(errorData.violations)
                    ? errorData.violations
                    : Array.isArray(errorData.errors)
                        ? errorData.errors
                        : [],
            };
        }

        const data = await response.json();
        console.log('Success response data:', data);
        return {
            success: true,
            employee: data,
        };
    } catch (error) {
        console.error('updateEmployee error:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Network error occurred',
        };
    }
};

export const deleteEmployees = async (token: string, ids: string[]): Promise<DeleteEmployeesResult> => {
    console.log('deleteEmployees service called with ids:', ids);
    console.log('API URL:', `${API_BASE_URL}/api/employees/delete`);

    try {
        const response = await fetch(`${API_BASE_URL}/api/employees/delete`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(ids),
        });

        console.log('Response status:', response.status);
        console.log('Response ok:', response.ok);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('Error response:', errorData);
            throw new Error(errorData.message || 'Failed to delete employees');
        }

        console.log('Delete operation successful');
        return {
            success: true,
        };
    } catch (error) {
        console.error('deleteEmployees error:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Network error occurred',
        };
    }
};

export const activateEmployees = async (token: string, ids: string[]): Promise<ActivateDeactivateResult> => {
    console.log('activateEmployees service called with ids:', ids);
    console.log('Token:', token ? 'Present' : 'Missing');
    console.log('API URL:', `${API_BASE_URL}/api/employees/activate`);
    console.log('Request body:', JSON.stringify(ids));

    try {
        console.log('About to send fetch request...');
        const response = await fetch(`${API_BASE_URL}/api/employees/activate`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(ids),
        });

        console.log('Response received - Status:', response.status);
        console.log('Response ok:', response.ok);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('Error response:', errorData);
            throw new Error(errorData.message || 'Failed to activate employees');
        }

        console.log('Activate operation successful');
        return {
            success: true,
        };
    } catch (error) {
        console.error('activateEmployees error:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Network error occurred',
        };
    }
};

export const deactivateEmployees = async (token: string, ids: string[]): Promise<ActivateDeactivateResult> => {
    console.log('deactivateEmployees service called with ids:', ids);
    console.log('Token:', token ? 'Present' : 'Missing');
    console.log('API URL:', `${API_BASE_URL}/api/employees/deactivate`);
    console.log(' Request body:', JSON.stringify(ids));

    try {
        console.log('About to send fetch request...');
        const response = await fetch(`${API_BASE_URL}/api/employees/deactivate`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(ids),
        });

        console.log('Response received - Status:', response.status);
        console.log('Response ok:', response.ok);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('Error response:', errorData);
            throw new Error(errorData.message || 'Failed to deactivate employees');
        }

        console.log('Deactivate operation successful');
        return {
            success: true,
        };
    } catch (error) {
        console.error('deactivateEmployees error:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Network error occurred',
        };
    }
};

export const addContract = async (token: string, userId: string, contractData: Contract): Promise<AddContractResult> => {
    console.log('addContract service called with:', contractData);
    console.log('API URL:', `${API_BASE_URL}/api/contract/add/${userId}`);

    try {
        const response = await fetch(`${API_BASE_URL}/api/contract/add/${userId}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify((({contractNumber: _ignored, ...rest}) => rest)(contractData)), // number is generated by the backend
        });

        console.log('Response status:', response.status);
        console.log('Response ok:', response.ok);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('Error response:', errorData);
            return {
                success: false,
                statusCode: response.status,
                error: errorData.message || 'Failed to add contract',
                violations: Array.isArray(errorData.violations)
                    ? errorData.violations
                    : Array.isArray(errorData.errors)
                        ? errorData.errors
                        : [],
            };
        }

        const data = await response.json();
        console.log('Success response data:', data);
        return {
            success: true,
            contract: data,
        };
    } catch (error) {
        console.error('addContract error:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Network error occurred',
        };
    }
};

export const getContracts = async (token: string, userId: string, limit: number = 10, page: number = 1): Promise<ContractsResult> => {
    console.log('getContracts service called with userId:', userId, 'limit:', limit, 'page:', page);
    console.log('API URL:', `${API_BASE_URL}/api/contract/${userId}/get?limit=${limit}&page=${page}`);

    try {
        const response = await fetch(`${API_BASE_URL}/api/contract/${userId}/get?limit=${limit}&page=${page}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        console.log('Response status:', response.status);
        console.log('Response ok:', response.ok);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('Error response:', errorData);
            throw new Error(errorData.message || 'Failed to fetch contracts');
        }

        const data = await response.json();
        console.log('Success response data:', data);
        return {
            success: true,
            contracts: data.contracts,
            totalRecords: data.totalRecords,
        };
    } catch (error) {
        console.error('getContracts error:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Network error occurred',
        };
    }
};

export const terminateContract = async (token: string, userId: string | number, contractId: string): Promise<TerminateContractResult> => {
    console.log('terminateContract service called with userId:', userId, 'contractId:', contractId);
    console.log('API URL:', `${API_BASE_URL}/api/contract/${userId}/update/${contractId}`);

    try {
        const response = await fetch(`${API_BASE_URL}/api/contract/${userId}/update/${contractId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
                status: 'terminated'
            })
        });

        console.log('Response status:', response.status);
        console.log('Response ok:', response.ok);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('Error response:', errorData);
            throw new Error(errorData.message || 'Failed to terminate contract');
        }

        const data = await response.json();
        console.log('Success response data:', data);

        return {
            success: data.success,
            contractId: data.contractId,
        };
    } catch (error) {
        console.error('terminateContract error:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Network error occurred',
        };
    }
};

export const searchEmployees = async (token: string, query: string): Promise<EmployeesResult> => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/employees/search?query=${encodeURIComponent(query)}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || 'Failed to search employees');
        }

        const data = await response.json();
        return {
            success: true,
            employees: data,
        };
    } catch (error) {
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Network error occurred',
        };
    }
};

